import type { CSSProperties, ReactNode } from "react";
import Modal from "./Modal";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";

export default function ConfirmModal({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  busy = false,
  tone = "danger",
  onConfirm,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  tone?: "danger" | "primary";
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
  children?: ReactNode;
}) {
  const confirmStyle =
    tone === "danger"
      ? { background: colors.berryRose, color: "#fff", borderColor: "transparent" }
      : { background: colors.navy, color: "#fff", borderColor: "transparent" };

  return (
    <Modal open={open} title={title} onClose={busy ? () => undefined : onClose}>
      {description && <p style={styles.description}>{description}</p>}
      {children}
      <div style={styles.actions}>
        <button type="button" onClick={onClose} disabled={busy} style={styles.cancelButton}>
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          style={{ ...styles.confirmButton, ...confirmStyle }}
        >
          {busy ? "Working..." : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

const styles = {
  description: {
    margin: "0 0 1rem",
    color: "rgba(8,45,61,0.75)",
    fontFamily: fonts.body,
    fontSize: 14,
    lineHeight: 1.5,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "0.7rem",
    marginTop: "1.25rem",
  },
  cancelButton: {
    background: colors.cream,
    border: "1px solid rgba(201,147,76,0.55)",
    color: colors.navy,
    borderRadius: 7,
    padding: "9px 16px",
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
  },
  confirmButton: {
    border: "1px solid transparent",
    borderRadius: 7,
    padding: "9px 16px",
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
  },
} satisfies Record<string, CSSProperties>;
